import { forwardRef, Fragment, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Button,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
} from "@chakra-ui/react";
import type { Consultant } from "@models/Consultant";
import type { Job } from "@models/Job";
import type {
	Consultant as ScheduleConsultant,
	JobSchedule,
	ScheduleApplication,
	ScheduleJobApplications,
	ScheduleMatchingConsultant,
	ScheduleShiftInput,
} from "@models/Schedule";
import { cn } from "@/lib/utils";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import ConsultantSelectMenu from "@components/createOrder/ConsultantSelectMenu";
import ApplicantList, { getConsultantName } from "./ApplicantList";
import JobInfoPopover from "./JobInfoPopover";
import ShiftContextMenu from "./ShiftContextMenu";
import ShiftPill from "./ShiftPill";
import ShiftTimesPopover, { ShiftTimesValue } from "./ShiftTimesPopover";
import {
	buildShiftTimes,
	dateKey,
	formatHours,
	getCellVariant,
	getConsultantForShift,
	getDayTotals,
	getExtensionConsultant,
	getLaneSlots,
	getLatestShiftTimes,
	getRepeatDates,
	getRequestDeclinedShiftIds,
	getRowsForWeek,
	getWeekDates,
	getWeekLanes,
	getWeekStartsBetween,
	moveShiftToDate,
	nextDraftId,
	ShiftRepeatPreset,
	WeekScheduleDraft,
	WeekScheduleRow,
	WeekScheduleShift,
} from "./weekSchedule.utils";

export type ShiftListChange = (shifts: ScheduleShiftInput[]) => ScheduleShiftInput[];

interface Props {
	jobs: JobSchedule[];
	/** Job details (shift breaks, description), loaded separately from the schedule */
	detailsByJobId: Record<string, Job>;
	applicationsByJobId: Record<string, ScheduleJobApplications>;
	competenceLabelByKey: Map<string, string>;
	/** Monday (YYYY-MM-DD) of the first and the last week to show */
	startWeek: string;
	endWeek: string;
	/** Called when scrolled close to the first/last week, to load more weeks */
	onReachStart: () => void;
	onReachEnd: () => void;
	/** More weeks are loading before the first/after the last week */
	isLoadingStart?: boolean;
	isLoadingEnd?: boolean;
	/** Consultants matching the job, for the "Bjud in konsulter" drawer */
	loadConsultants: (jobId: string) => Promise<ScheduleMatchingConsultant[]>;
	/** Resolves with whether the shifts were saved */
	onUpdateShifts: (jobId: string, message: string, change: ShiftListChange) => Promise<boolean>;
	/** Rejects when the invitations could not be sent */
	onInvite: (jobId: string, consultantIds: string[], allowLaterBooking?: boolean) => Promise<void>;
	isInviting?: boolean;
	onBook: (jobId: string, applicationId: string) => void;
	/**
	 * Draft mode (the single-job view): shifts added by clicking empty days, and
	 * copies of shifts, become drafts in the unmanned lane that are sent
	 * together with `submitDrafts()` instead of being saved at once.
	 */
	drafts?: WeekScheduleDraft[];
	onDraftsChange?: (drafts: WeekScheduleDraft[]) => void;
	/** Monday (YYYY-MM-DD) of the week to open on; the current week by default */
	focusWeek?: string;
	/** Whether the job popover links to the single-job view */
	showJobScheduleLink?: boolean;
}

interface ShiftTarget {
	job: JobSchedule;
	shift: WeekScheduleShift;
	consultant?: ScheduleConsultant;
	isUnmanned: boolean;
	position: { x: number; y: number };
}

interface TimesSession {
	job: JobSchedule;
	shift: WeekScheduleShift;
	anchor: { x: number; y: number };
}

/** A new draft being filled in for a day */
interface DraftSession {
	job: JobSchedule;
	date: string;
	anchor: { x: number; y: number };
}

interface InviteSession {
	job: JobSchedule;
	consultants?: ScheduleMatchingConsultant[];
	/** Message shown instead of the list when the consultants could not be loaded */
	loadError?: string;
}

/** Shown after shifts were added to a job with a booked consultant */
interface ExtensionInfo {
	job: JobSchedule;
	shiftsCount: number;
	consultant?: { id: string; firstName?: string; lastName?: string };
}

const STAFFABLE_JOB_STATUSES = ["OPEN", "BOOKED_WITH_UNMANNED_SHIFTS"];
const EDITABLE_JOB_STATUSES = [...STAFFABLE_JOB_STATUSES, "BOOKED"];

export interface WeekScheduleHandle {
	/** Smooth-scrolls the current week into view */
	scrollToToday: () => void;
	/** Sends the drafts in one request. Resolves with whether they were saved. */
	submitDrafts: () => Promise<boolean>;
}

/** Space kept below the scroll area, so it ends above the bottom of the window */
const SCROLL_AREA_BOTTOM_GAP = 24;
const SCROLL_AREA_MIN_HEIGHT = 360;

// The label column shrinks with the grid on narrower screens so the days get the room
const GRID_CLASS =
	"grid min-w-[1040px] gap-1 grid-cols-[minmax(150px,17%)_repeat(7,minmax(0,1fr))] 2xl:grid-cols-[minmax(190px,230px)_repeat(7,minmax(0,1fr))]";

/**
 * Weeks of shifts in a scroll area that fills the rest of the window, like the
 * dashboard's shift planner: it opens on the current week (with the week
 * before peeking in above) and scrolling close to the first or last week asks
 * for more weeks, which the parent limits.
 */
const WeekSchedule = forwardRef<WeekScheduleHandle, Props>(function WeekSchedule(
	{
		jobs,
		detailsByJobId,
		applicationsByJobId,
		competenceLabelByKey,
		startWeek,
		endWeek,
		onReachStart,
		onReachEnd,
		isLoadingStart = false,
		isLoadingEnd = false,
		loadConsultants,
		onUpdateShifts,
		onInvite,
		isInviting = false,
		onBook,
		drafts = [],
		onDraftsChange,
		focusWeek,
		showJobScheduleLink = true,
	},
	ref,
) {
	const isDraftMode = !!onDraftsChange;
	const [menuTarget, setMenuTarget] = useState<ShiftTarget>();
	const [applicantsTarget, setApplicantsTarget] = useState<ShiftTarget>();
	const [timesSession, setTimesSession] = useState<TimesSession>();
	const [draftSession, setDraftSession] = useState<DraftSession>();
	const [removeTarget, setRemoveTarget] = useState<ShiftTarget>();
	const [inviteSession, setInviteSession] = useState<InviteSession>();
	const [extensionInfo, setExtensionInfo] = useState<ExtensionInfo>();
	const cancelRemoveRef = useRef<any>(null);

	// The schedule endpoint has neither shift breaks nor consultant profiles: breaks,
	// pictures and presentations come with the job details, home towns with the applications
	const jobsWithDetails = useMemo(
		() =>
			jobs.map((job) => {
				const details = detailsByJobId[job.id];
				const breakByShiftId = new Map((details?.shifts || []).map((s: any) => [s.id, s.break]));
				const profileByConsultantId = new Map<string, ScheduleConsultant["profile"]>();
				(applicationsByJobId[job.id]?.applications || []).forEach(({ consultant }) => {
					profileByConsultantId.set(consultant.id, {
						profilePicture: consultant.profile?.profilePicture,
						city: consultant.profile?.address?.city,
					});
				});
				(details?.applications || []).forEach(({ consultant }: any) => {
					if (!consultant?.id) return;
					profileByConsultantId.set(consultant.id, {
						...profileByConsultantId.get(consultant.id),
						profilePicture: consultant.profile?.profilePicture ?? profileByConsultantId.get(consultant.id)?.profilePicture,
						presentation: consultant.profile?.presentation,
					});
				});
				const draftShifts: WeekScheduleShift[] = drafts.map((d) => ({ ...d, isDraft: true }));
				return {
					...job,
					shifts: [...job.shifts.map((s) => ({ ...s, break: breakByShiftId.get(s.id) })), ...draftShifts],
					applications: job.applications?.map((a) => {
						const profile = profileByConsultantId.get(a.consultant.id);
						return profile ? { ...a, consultant: { ...a.consultant, profile } } : a;
					}),
				};
			}),
		[jobs, detailsByJobId, applicationsByJobId, drafts],
	);

	const weeks = useMemo(
		() => getWeekStartsBetween(startWeek, endWeek).map((monday) => getWeekDates(moment(monday))),
		[startWeek, endWeek],
	);

	const scrollRef = useRef<HTMLDivElement>(null);
	const weekRefs = useRef(new Map<string, HTMLDivElement>());
	const topSentinelRef = useRef<HTMLDivElement>(null);
	const bottomSentinelRef = useRef<HTMLDivElement>(null);
	const [maxHeight, setMaxHeight] = useState<number>();

	// The scroll area fills the window below its top edge
	useLayoutEffect(() => {
		const update = () => {
			const container = scrollRef.current;
			if (!container) return;
			const top = container.getBoundingClientRect().top + window.scrollY;
			setMaxHeight(Math.max(SCROLL_AREA_MIN_HEIGHT, window.innerHeight - top - SCROLL_AREA_BOTTOM_GAP));
		};
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	const getWeekTop = (monday: string) => weekRefs.current.get(monday)?.offsetTop;
	const currentWeek = dateKey(moment().startOf("isoWeek"));
	const openingWeek = focusWeek ?? currentWeek;

	// Open on the focused (by default the current) week, with the week before it peeking in above for context
	const hasFocused = useRef(false);
	useLayoutEffect(() => {
		const container = scrollRef.current;
		const target = getWeekTop(openingWeek);
		if (hasFocused.current || maxHeight === undefined || !container || target === undefined) return;
		const weekBefore = dateKey(moment(openingWeek).subtract(1, "week"));
		container.scrollTop = Math.max(getWeekTop(weekBefore) ?? target, target - container.clientHeight * 0.3);
		hasFocused.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [maxHeight]);

	useImperativeHandle(ref, () => ({
		scrollToToday() {
			const target = getWeekTop(currentWeek);
			if (target !== undefined) scrollRef.current?.scrollTo({ top: target, behavior: "smooth" });
		},
		async submitDrafts() {
			const job = jobs[0];
			if (!job || !drafts.length || !onDraftsChange) return false;
			const saved = await addShifts(job, drafts.length === 1 ? "Passet har lagts till" : "Passen har lagts till", () =>
				drafts.map(({ start, end, break: breakVal }) => ({ start, end, break: breakVal })),
			);
			if (saved) onDraftsChange([]);
			return saved;
		},
	}));

	// Keep the viewport still when weeks are added above it: the distance to the
	// bottom is read during render (before the DOM changes) and restored after.
	// Native scroll anchoring is off on the container since Safari lacks it.
	const renderedStartWeek = useRef(startWeek);
	const bottomDistance = useRef<number>();
	if (startWeek !== renderedStartWeek.current) {
		const container = scrollRef.current;
		if (container && startWeek < renderedStartWeek.current)
			bottomDistance.current = container.scrollHeight - container.scrollTop;
		renderedStartWeek.current = startWeek;
	}
	useLayoutEffect(() => {
		const container = scrollRef.current;
		if (container && bottomDistance.current !== undefined)
			container.scrollTop = container.scrollHeight - bottomDistance.current;
		bottomDistance.current = undefined;
	}, [startWeek]);

	// Scrolling close to an edge asks for more weeks. Observed again whenever the
	// weeks change, so an edge that is still in view after loading asks again.
	const reachHandlers = useRef({ onReachStart, onReachEnd });
	reachHandlers.current = { onReachStart, onReachEnd };
	const isReady = maxHeight !== undefined;
	useEffect(() => {
		const container = scrollRef.current;
		if (!isReady || !container || typeof IntersectionObserver === "undefined") return;
		const observer = new IntersectionObserver(
			(entries) =>
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					if (entry.target === topSentinelRef.current) reachHandlers.current.onReachStart();
					else reachHandlers.current.onReachEnd();
				}),
			{ root: container, rootMargin: "300px 0px" },
		);
		if (topSentinelRef.current) observer.observe(topSentinelRef.current);
		if (bottomSentinelRef.current) observer.observe(bottomSentinelRef.current);
		return () => observer.disconnect();
	}, [isReady, startWeek, endWeek]);

	const getPending = (jobId: string) =>
		(applicationsByJobId[jobId]?.applications || []).filter((a) => a.status === "PENDING_ANSWER");
	const getInvitations = (jobId: string) => applicationsByJobId[jobId]?.invitations || [];
	const getOpenInvitations = (jobId: string) => getInvitations(jobId).filter((i) => i.status === "OPEN");

	/** Pending applications are for the job's unmanned shifts that accepting one would book */
	const isShiftAppliedFor = (jobId: string, shiftId: string) => {
		const ids = applicationsByJobId[jobId]?.wouldBookShiftIds || [];
		return !ids.length || ids.includes(shiftId);
	};

	const getBookedByTitle = (job: JobSchedule, shiftId: string) => {
		const application = (applicationsByJobId[job.id]?.applications || []).find(
			(a) => a.status === "ACCEPTED" && a.shifts?.some((s) => s.id === shiftId),
		);
		const acceptedBy = application?.metadata?.acceptedBy;
		if (!acceptedBy || typeof acceptedBy === "string" || !acceptedBy.firstName) return undefined;
		const at = application?.metadata?.acceptedAt;
		return `Bokad av ${getConsultantName(acceptedBy)}${at ? ` ${moment(at).format("D/M YYYY")}` : ""}`;
	};

	const getLockedReason = ({ job, shift, isUnmanned }: ShiftTarget) => {
		if (shift.isDraft) return undefined;
		if (moment(shift.start).isBefore(moment())) return "Passet har redan startat och kan inte ändras.";
		if (!EDITABLE_JOB_STATUSES.includes(job.status)) return "Uppdraget kan inte längre ändras.";
		if (!isUnmanned) return "Passet är bokat. Kontakta oss så hjälper vi dig att ändra det.";
		return undefined;
	};

	/**
	 * Adding shifts to a job with a booked consultant makes them an extension
	 * request to that consultant, which is explained in an info box afterwards.
	 * Resolves with whether the shifts were saved.
	 */
	const addShifts = async (job: JobSchedule, message: string, added: (shifts: ScheduleShiftInput[]) => ScheduleShiftInput[]) => {
		const consultant = getExtensionConsultant(job);
		let shiftsCount = 0;
		const saved = await onUpdateShifts(job.id, consultant ? "Förlängning skapad" : message, (shifts) => {
			const newShifts = added(shifts);
			shiftsCount = newShifts.length;
			return [...shifts, ...newShifts];
		});
		if (saved && consultant && shiftsCount > 0) setExtensionInfo({ job, shiftsCount, consultant });
		return saved;
	};

	/** In draft mode new shifts are kept locally until `submitDrafts()` */
	const addDrafts = (added: Omit<WeekScheduleDraft, "id">[]) =>
		onDraftsChange?.([...drafts, ...added.map((d) => ({ ...d, id: nextDraftId() }))]);

	const handleSubmitTimes = (value: ShiftTimesValue) => {
		if (!timesSession) return;
		const { job, shift } = timesSession;
		const times = { ...buildShiftTimes(dateKey(shift.start), value.from, value.to), break: value.break };
		if (shift.isDraft) onDraftsChange?.(drafts.map((d) => (d.id === shift.id ? { ...d, ...times } : d)));
		else
			onUpdateShifts(job.id, "Passets tider har ändrats", (shifts) =>
				shifts.map((s) => (s.id === shift.id ? { ...s, ...times } : s)),
			);
		setTimesSession(undefined);
	};

	const handleAddDraft = (value: ShiftTimesValue) => {
		if (!draftSession) return;
		addDrafts([{ ...buildShiftTimes(draftSession.date, value.from, value.to), break: value.break }]);
		setDraftSession(undefined);
	};

	const handleDuplicate = ({ job, shift }: ShiftTarget) => {
		const copy = { start: shift.start, end: shift.end, break: shift.break };
		if (isDraftMode) addDrafts([copy]);
		else addShifts(job, "Passet har duplicerats", () => [copy]);
	};

	const handleRepeat = ({ job, shift }: ShiftTarget, preset: ShiftRepeatPreset) => {
		const copies = (existing: { start: string }[]) => {
			const existingStarts = new Set(existing.map((s) => moment(s.start).toISOString()));
			return getRepeatDates(shift.start, preset)
				.map((date) => ({ ...moveShiftToDate(shift, date), break: shift.break }))
				.filter((copy) => !existingStarts.has(copy.start));
		};
		// The row's job carries the drafts too, so a repeat never doubles one
		if (isDraftMode) addDrafts(copies(job.shifts));
		else addShifts(job, "Passet har repeterats", copies);
	};

	const removeDraft = (shift: WeekScheduleShift) => onDraftsChange?.(drafts.filter((d) => d.id !== shift.id));

	/** Days from today on can take drafts, for jobs that can still be changed */
	const canAddDraft = (job: JobSchedule, date: moment.Moment) =>
		isDraftMode && EDITABLE_JOB_STATUSES.includes(job.status) && date.isSameOrAfter(moment(), "day");

	const handleConfirmRemove = () => {
		if (!removeTarget) return;
		const { job, shift } = removeTarget;
		onUpdateShifts(job.id, "Passet har tagits bort", (shifts) => {
			const remaining = shifts.filter((s) => s.id !== shift.id);
			if (!remaining.length) throw new Error("Uppdraget måste innehålla minst ett pass.");
			return remaining;
		});
		setRemoveTarget(undefined);
	};

	const openInviteDrawer = (job: JobSchedule) => {
		setInviteSession({ job });
		loadConsultants(job.id).then(
			(consultants) => setInviteSession((prev) => (prev?.job.id === job.id ? { ...prev, consultants } : prev)),
			(error: Error) =>
				setInviteSession((prev) => (prev?.job.id === job.id ? { ...prev, loadError: error.message } : prev)),
		);
	};

	/** Consultants already asked, applied or booked: shown pre-selected in the drawer and cannot be toggled */
	const getLockedConsultants = (session: InviteSession) => {
		const labelById: Record<string, string> = {};
		(session.consultants || []).forEach((c) => {
			if (c.isBooked) labelById[c.id] = "Bokad";
			else if (c.hasPendingApplication) labelById[c.id] = "Ansökt";
			else if (c.isInvited) labelById[c.id] = "Önskad";
		});
		getOpenInvitations(session.job.id).forEach((i) => (labelById[i.consultant.id] ||= "Önskad"));
		getPending(session.job.id).forEach((a) => (labelById[a.consultant.id] = "Ansökt"));
		return labelById;
	};

	/** The drawer is the one from the booking flow and takes its `Consultant` shape */
	const toDrawerConsultant = ({ id, firstName, lastName, hasCustomerExperience, profile }: ScheduleMatchingConsultant): Consultant => ({
		id,
		firstName,
		lastName,
		hasCustomerExperience,
		// The matching endpoint has no presentation, the home town is shown in its place
		profile: { profilePicture: profile?.profilePicture, presentation: profile?.address?.city },
	});

	const drawerConsultants = useMemo(
		() => (inviteSession?.consultants || []).map(toDrawerConsultant),
		[inviteSession?.consultants],
	);

	const drawerMenuData = useMemo(() => {
		if (!inviteSession) return { selectedConsultants: [], shouldSystemChooseIfSelectedConsultantsNotAvailable: null };
		const locked = getLockedConsultants(inviteSession);
		return {
			selectedConsultants: drawerConsultants.filter((c) => locked[c.id]),
			shouldSystemChooseIfSelectedConsultantsNotAvailable: detailsByJobId[inviteSession.job.id]?.allowLaterBooking ?? null,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inviteSession, drawerConsultants, detailsByJobId, applicationsByJobId]);

	const handleSendInvitations = async (selected: Consultant[], allowLaterBooking: boolean) => {
		if (!inviteSession) return;
		const { job } = inviteSession;
		const locked = getLockedConsultants(inviteSession);
		const consultantIds = selected.map((c) => c.id).filter((id) => !locked[id]);
		const currentAllowLaterBooking = detailsByJobId[job.id]?.allowLaterBooking;
		try {
			await onInvite(job.id, consultantIds, allowLaterBooking === currentAllowLaterBooking ? undefined : allowLaterBooking);
			setInviteSession(undefined);
		} catch {
			// Reported by the caller, the drawer stays open
		}
	};

	const renderRow = (row: WeekScheduleRow, weekDates: moment.Moment[]) => {
		const { job } = row;
		// Only shown when the user asks for declined jobs; their shifts are marked red
		const isDeclined = job.status === "DECLINED";
		const pending = getPending(job.id);
		const invited = getOpenInvitations(job.id);
		const lanes = getWeekLanes(row, weekDates);
		const requestDeclinedIds = getRequestDeclinedShiftIds(job.shifts as WeekScheduleShift[], getInvitations(job.id));

		return (
			<Fragment key={row.key}>
				<div className="min-w-0 rounded bg-white/60 px-3 py-1.5">
					<div className="flex items-center gap-2">
						<div className="truncate text-sm font-semibold text-gray-900">
							{competenceLabelByKey.get(job.competence.service) || job.competence.service}
						</div>
						{isDeclined && (
							<span className="shrink-0 rounded-sm bg-[#FFEBEB] px-1.5 py-0.5 text-[11px] font-semibold leading-none text-[#EB5757]">
								Nekat
							</span>
						)}
					</div>
					<JobInfoPopover
						orderNumber={job.orderNumber}
						details={detailsByJobId[job.id]}
						extendHref={showJobScheduleLink && EDITABLE_JOB_STATUSES.includes(job.status) ? `/uppdrag/${job.id}/schema` : undefined}
						extendLabel={job.status === "BOOKED" ? "Förläng uppdrag" : "Schema & pass"}
					>
						<Link to={`/uppdrag/${job.id}`} className="block truncate text-xs text-gray-600 hover:underline">
							#{job.orderNumber} · {job.customer.name}
						</Link>
					</JobInfoPopover>
				</div>
				{weekDates.map((date) => {
					const key = dateKey(date);
					const isToday = date.isSame(moment(), "day");
					const isAddable = canAddDraft(job, date);
					return (
						<div
							key={key}
							className={cn(
								"group flex flex-col gap-1 rounded border border-solid bg-white p-1",
								isToday ? "border-brand ring-1 ring-inset ring-brand" : "border-gray-200",
								isAddable && "cursor-pointer hover:bg-[#F5FBFE]",
							)}
							title={isAddable ? "Klicka för att lägga till ett pass" : undefined}
							onClick={(e) => {
								// Pills handle their own clicks; the rest of the cell adds a draft
								if (!isAddable || (e.target as HTMLElement).closest("button")) return;
								setDraftSession({ job, date: key, anchor: { x: e.clientX, y: e.clientY } });
							}}
						>
							{getLaneSlots(row, key, lanes).map((shifts, lane) => {
								// Same height as a pill, so the lanes below line up with the other days
								if (!shifts.length) return <div key={lanes[lane]} className="min-h-[52px]" />;
								return shifts.map((shift) => {
									const consultant = getConsultantForShift(job, shift.id);
									const target = { job, shift, consultant, isUnmanned: !consultant };
									const isAppliedFor = !consultant && !shift.isDraft && isShiftAppliedFor(job.id, shift.id);
									return (
										<ShiftPill
											key={shift.id}
											shift={shift}
											variant={getCellVariant(job, shift, consultant, requestDeclinedIds.has(shift.id))}
											consultant={consultant}
											title={consultant ? getBookedByTitle(job, shift.id) : undefined}
											pendingCount={isAppliedFor ? pending.length : 0}
											invitedCount={isAppliedFor ? invited.length : 0}
											onOpenMenu={(e) => setMenuTarget({ ...target, position: { x: e.clientX, y: e.clientY } })}
											onOpenApplicants={(e) => setApplicantsTarget({ ...target, position: { x: e.clientX, y: e.clientY } })}
										/>
									);
								});
							})}
							{isAddable && (
								<div className="flex flex-1 items-center justify-center py-1 text-gray-300 opacity-0 group-hover:opacity-100">
									<Plus className="h-4 w-4" />
								</div>
							)}
						</div>
					);
				})}
			</Fragment>
		);
	};

	const loadingWeeksRow = <div className="py-2 text-center text-sm text-gray-500">Hämtar veckor…</div>;

	return (
		<div className="w-full">
			<div
				ref={scrollRef}
				style={{ maxHeight }}
				className="relative flex w-full flex-col gap-4 overflow-auto pb-2 [overflow-anchor:none]"
			>
				<div ref={topSentinelRef} className="h-px shrink-0" />
				{isLoadingStart && loadingWeeksRow}
				{weeks.map((weekDates) => {
					const rows = getRowsForWeek(jobsWithDetails, weekDates, isDraftMode);
					const monday = weekDates[0];
					return (
						<div
							key={dateKey(monday)}
							ref={(el) => {
								if (el) weekRefs.current.set(dateKey(monday), el);
								else weekRefs.current.delete(dateKey(monday));
							}}
							className={cn(GRID_CLASS, "shrink-0")}
						>
							<div className="flex items-center rounded border border-solid border-gray-200 bg-white px-3 py-2 text-sm font-semibold">
								Vecka {monday.isoWeek()}, {monday.locale("sv").format(monday.isSame(moment(), "year") ? "MMM" : "MMM YYYY")}
							</div>
							{weekDates.map((date, i) => {
								const totals = getDayTotals(rows, dateKey(date));
								const isToday = date.isSame(moment(), "day");
								return (
									<div
										key={dateKey(date)}
										className={cn(
											"rounded border border-solid bg-white px-2 py-1.5",
											isToday ? "border-brand ring-1 ring-inset ring-brand" : "border-gray-200",
										)}
									>
										<div className={cn("text-sm font-semibold capitalize", i >= 5 && "text-[#EB5757]")}>
											{date.locale("sv").format("ddd D")}
										</div>
										<div className="truncate text-xs text-gray-500">
											{formatHours(totals.hours)} h
											{totals.breakHours > 0 && ` / ${formatHours(totals.breakHours)} h rast`}
										</div>
									</div>
								);
							})}
							{rows.map((row) => renderRow(row, weekDates))}
							{!rows.length && (
								<div className="col-span-8 rounded bg-white/60 py-2 text-center text-sm text-gray-500">
									Inga pass denna vecka
								</div>
							)}
						</div>
					);
				})}
				{isLoadingEnd && loadingWeeksRow}
				<div ref={bottomSentinelRef} className="h-px shrink-0" />
			</div>

			{menuTarget && (
				<ShiftContextMenu
					position={menuTarget.position}
					label={`${menuTarget.shift.isDraft ? "Nytt pass · " : menuTarget.consultant ? `${getConsultantName(menuTarget.consultant)} · ` : ""}${moment(
						menuTarget.shift.start,
					)
						.locale("sv")
						.format("ddd D/M")} · ${moment(menuTarget.shift.start).format("HH:mm")} - ${moment(menuTarget.shift.end).format(
						"HH:mm",
					)}`}
					lockedReason={getLockedReason(menuTarget)}
					canCopy={EDITABLE_JOB_STATUSES.includes(menuTarget.job.status)}
					copiesAsDrafts={isDraftMode}
					canStaff={
						menuTarget.isUnmanned &&
						!menuTarget.shift.isDraft &&
						STAFFABLE_JOB_STATUSES.includes(menuTarget.job.status) &&
						moment(menuTarget.shift.end).isAfter(moment())
					}
					pendingApplications={
						isShiftAppliedFor(menuTarget.job.id, menuTarget.shift.id) ? getPending(menuTarget.job.id) : []
					}
					onChangeTimes={() => setTimesSession({ job: menuTarget.job, shift: menuTarget.shift, anchor: menuTarget.position })}
					onDuplicate={() => handleDuplicate(menuTarget)}
					onRepeat={(preset) => handleRepeat(menuTarget, preset)}
					onRemove={() => (menuTarget.shift.isDraft ? removeDraft(menuTarget.shift) : setRemoveTarget(menuTarget))}
					onInvite={() => {
						openInviteDrawer(menuTarget.job);
						setMenuTarget(undefined);
					}}
					onBook={(application: ScheduleApplication) => onBook(menuTarget.job.id, application.id)}
					onClose={() => setMenuTarget(undefined)}
				/>
			)}

			<ShiftTimesPopover
				anchor={timesSession?.anchor}
				title="Ändra tider"
				subtitle={
					timesSession &&
					`${moment(timesSession.shift.start).locale("sv").format("dddd D MMMM")} · #${timesSession.job.orderNumber}`
				}
				submitLabel="Spara"
				initialValue={
					timesSession && {
						from: moment(timesSession.shift.start).format("HH:mm"),
						to: moment(timesSession.shift.end).format("HH:mm"),
						break: timesSession.shift.break || 0,
					}
				}
				onSubmit={handleSubmitTimes}
				onClose={() => setTimesSession(undefined)}
			/>

			<ShiftTimesPopover
				anchor={draftSession?.anchor}
				title="Nytt pass"
				subtitle={draftSession && `${moment(draftSession.date).locale("sv").format("dddd D MMMM")} · #${draftSession.job.orderNumber}`}
				submitLabel="Lägg till"
				// Starts from the job's latest shift (drafts included), which is usually what the next one looks like
				initialValue={draftSession && getLatestShiftTimes(draftSession.job.shifts as WeekScheduleShift[])}
				onSubmit={handleAddDraft}
				onClose={() => setDraftSession(undefined)}
			/>

			<Popover open={!!applicantsTarget} onOpenChange={(open) => !open && setApplicantsTarget(undefined)}>
				<PopoverAnchor asChild>
					<span
						className="pointer-events-none fixed h-px w-px"
						style={{ left: applicantsTarget?.position.x ?? 0, top: applicantsTarget?.position.y ?? 0 }}
					/>
				</PopoverAnchor>
				<PopoverContent align="start" className="w-80">
					{applicantsTarget && (
						<>
							<div className="mb-2 text-sm font-semibold text-gray-900">
								Ansökningar · #{applicantsTarget.job.orderNumber}
							</div>
							<ApplicantList
								applications={getPending(applicantsTarget.job.id)}
								invitations={getOpenInvitations(applicantsTarget.job.id)}
								canBook={STAFFABLE_JOB_STATUSES.includes(applicantsTarget.job.status)}
								onBook={(application) => {
									onBook(applicantsTarget.job.id, application.id);
									setApplicantsTarget(undefined);
								}}
							/>
						</>
					)}
				</PopoverContent>
			</Popover>

			<ConsultantSelectMenu
				isOpen={!!inviteSession}
				onClose={() => setInviteSession(undefined)}
				menuData={drawerMenuData}
				onChangeMenuData={(data) =>
					handleSendInvitations(data.selectedConsultants, !!data.shouldSystemChooseIfSelectedConsultantsNotAvailable)
				}
				consultantsList={drawerConsultants}
				isLoading={!!inviteSession && !inviteSession.consultants && !inviteSession.loadError}
				lockedConsultantLabelById={inviteSession ? getLockedConsultants(inviteSession) : {}}
				isSubmitting={isInviting}
				description={
					inviteSession?.loadError ||
					`Välj de konsulter du vill önska till uppdrag #${inviteSession?.job.orderNumber ?? ""}. De får en förfrågan direkt och kan acceptera eller neka.`
				}
			/>

			<Modal isOpen={!!extensionInfo} onClose={() => setExtensionInfo(undefined)} isCentered>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader fontSize="lg" fontWeight={600}>
						Förlängning skapad
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						{extensionInfo && (
							<div className="flex flex-col gap-2 text-sm text-gray-700">
								<p className="m-0">
									{extensionInfo.shiftsCount === 1 ? "Passet har lagts till" : `${extensionInfo.shiftsCount} pass har lagts till`} på
									uppdrag #{extensionInfo.job.orderNumber} som en förlängning.
								</p>
								<p className="m-0">
									{extensionInfo.consultant ? getConsultantName(extensionInfo.consultant) : "Konsulten"} har fått en
									förfrågan om att ta {extensionInfo.shiftsCount === 1 ? "passet" : "passen"}. Tills konsulten svarat visas{" "}
									{extensionInfo.shiftsCount === 1 ? "det som obemannat" : "de som obemannade"} med texten ”Inväntar svar”.
								</p>
							</div>
						)}
					</ModalBody>
					<ModalFooter>
						<Button colorScheme="telegram" onClick={() => setExtensionInfo(undefined)}>
							OK
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			<AlertDialog isOpen={!!removeTarget} leastDestructiveRef={cancelRemoveRef} onClose={() => setRemoveTarget(undefined)} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight={600}>
							Ta bort pass
						</AlertDialogHeader>
						<AlertDialogBody>
							{removeTarget &&
								`Vill du ta bort passet ${moment(removeTarget.shift.start).locale("sv").format("dddd D/M HH:mm")} - ${moment(
									removeTarget.shift.end,
								).format("HH:mm")} från uppdrag #${removeTarget.job.orderNumber}?`}
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRemoveRef} onClick={() => setRemoveTarget(undefined)}>
								Avbryt
							</Button>
							<Button colorScheme="red" onClick={handleConfirmRemove} ml={3}>
								Ta bort
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</div>
	);
});

export default WeekSchedule;
